import { useEffect, useMemo, useState } from "react";
import { Link, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  Avatar, ChevronIcon, CommandPalette, EyeIcon, LogoutIcon, MenuButton, MoonIcon, RefreshIcon, SearchIcon, SettingsIcon, SparkIcon, SunIcon, useHotkey, type Command, type MenuItem,
} from "@signet/ui";
import { allowedBackgrounds, api, applyTheme, currentViewAs, desktop, isDesktop, isMacDesktop, newerBuildAvailable, reloadApp, setTheme, ByNumero, SignetMark, startBackgrounds, stopViewAs, themeFor, useMe, useTheme } from "@signet/web-kit";
import { navGroups, webModules } from "../../../../modules/registry.web.ts";
import { rememberVisit } from "../quick/recents.ts";
import { AssistantPanel } from "./assistant.tsx";
import { ADMIN_PLACES, CORE_PLACES, quickPlaces, type Place } from "./places.ts";
import { useTopbarHeight } from "./topbar-height.ts";

/* ──────────────────────────────────────────────────────────────────────────
   The frame around every page.

   The nav holds at most seven places, and only places this person can open,
   plus any a module marks as coming soon, which open nothing. A group (AI
   Team) is one place in the bar that opens to list its modules' places.
   Everything else is one Cmd or Ctrl K away in the palette, whose commands
   are filtered by the same permissions, so it never offers a page that would
   answer "you can't". Cmd or Ctrl J opens the assistant from anywhere.
   ────────────────────────────────────────────────────────────────────────── */

// A promised place: it takes its turn in the bar like any other, but has no
// page, so it is drawn as text rather than a link and never becomes a command.
type SoonPlace = { soon: true; label: string; icon: React.ComponentType<{ size?: number }> };
// Several modules' places behind one entry; navGroups in the web registry
// says which. Only places the person can open are in it, and it is left out
// when that is none, so it never opens onto an empty menu.
type GroupPlace = { group: string; label: string; icon: React.ComponentType<{ size?: number }>; places: Place[] };
type NavPlace = Place | SoonPlace | GroupPlace;
const isSoon = (p: NavPlace): p is SoonPlace => "soon" in p;
const isGroup = (p: NavPlace): p is GroupPlace => "group" in p;
const BAR_PLACES = 7;

export function Shell() {
  const { me, can } = useMe();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const [palette, setPalette] = useState(false);
  const [assistant, setAssistant] = useState(false);
  const frame = useTopbarHeight<HTMLDivElement, HTMLElement>();
  const viewingAs = currentViewAs();
  const theme = useTheme();

  // Each person's own theme. A preview keeps the owner's, since /api/me then
  // answers as the person being previewed.
  const username = me?.user.username;
  useEffect(() => {
    if (username && !viewingAs) applyTheme(themeFor(username));
  }, [username]);
  const switchTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    if (username && !viewingAs) setTheme(username, next);
    else applyTheme(next);
  };

  // Seven places in the bar: Now, then modules in registry order, a group
  // counting as one. The rest, and the admin pages, sit under More, so the
  // bar never grows past what fits and a new module never pushes Now off it.
  const { inNav, overflow, places } = useMemo(() => {
    const allowed = (p: Place) => !p.needs || can(p.needs);
    const groupOf = new Map(navGroups.flatMap((g) => g.modules.map((id) => [id, g] as const)));
    const groups = new Map<string, GroupPlace>();
    const modulePlaces: NavPlace[] = webModules.flatMap((m): NavPlace[] => {
      if (!m.nav) return [];
      if ("soon" in m.nav) return [{ soon: true, label: m.nav.label, icon: m.nav.icon }];
      const place: Place = { to: m.nav.to, label: m.nav.label, icon: m.nav.icon, needs: m.nav.needs };
      if (!allowed(place)) return [];
      const group = groupOf.get(m.id);
      if (!group) return [place];
      // The group sits where its first module the person can open would.
      const existing = groups.get(group.id);
      if (existing) {
        existing.places.push(place);
        return [];
      }
      const made: GroupPlace = { group: group.id, label: group.label, icon: group.icon, places: [place] };
      groups.set(group.id, made);
      return [made];
    });
    const main = [...CORE_PLACES.filter(allowed), ...modulePlaces];
    const admin = ADMIN_PLACES.filter(allowed);
    const rest = [...main.slice(BAR_PLACES), ...admin];
    const flat = [...main, ...admin].flatMap((p): Place[] => (isGroup(p) ? p.places : isSoon(p) ? [] : [p]));
    return { inNav: main.slice(0, BAR_PLACES), overflow: rest, places: flat };
  }, [me]);

  useHotkey({ key: "k", meta: true, inFields: true }, () => setPalette((open) => !open));
  useHotkey({ key: "j", meta: true, inFields: true }, () => can("signet.assistant:view") && setAssistant((open) => !open));
  useEffect(() => desktop?.onOpenAssistant(() => setAssistant(true)), []);

  // The desktop quick bar lists the places and creators opened lately, from
  // this window, however they were reached.
  useEffect(() => {
    if (me) rememberVisit(pathname, quickPlaces(can));
  }, [pathname, me]);

  // Modules' background work lives here because the shell only renders for a
  // signed-in person and unmounts on sign-out, which is exactly its lifetime.
  // Keyed on who they are and which backgrounds they may run, so a refetch of
  // /api/me that changes nothing leaves them running, and a lost permission
  // stops the one it covered at the next refetch.
  const backgrounds = me ? `${me.user.id}:${allowedBackgrounds(webModules, can).join(",")}` : "";
  useEffect(() => (me ? startBackgrounds(webModules, can, { userId: me.user.id }) : undefined), [backgrounds]);
  // Background work has to keep running with the window hidden, so the desktop
  // app must not unload the page while there is any.
  useEffect(() => void desktop?.keepLoaded?.(allowedBackgrounds(webModules, can).length > 0).catch(() => {}), [backgrounds]);

  // The desktop app shows a reload button, with a dot once a newer version
  // has been deployed. A browser tab has its own reload.
  const [newer, setNewer] = useState(false);
  useEffect(() => {
    if (!isDesktop) return;
    const check = () => document.visibilityState === "visible" && void newerBuildAvailable().then(setNewer);
    const timer = window.setInterval(check, 10 * 60_000);
    document.addEventListener("visibilitychange", check);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", check);
    };
  }, []);

  const signOut = async () => {
    await api("/api/auth/logout", { method: "POST", body: {} }).catch(() => {});
    queryClient.clear();
    navigate({ to: "/login", search: { next: undefined } });
  };

  const commands: Command[] = [
    ...places.map((p) => ({ id: `go:${p.to}`, label: p.label, keywords: p.keywords, group: "Go to", icon: <p.icon size={14} />, onSelect: () => navigate({ to: p.to }) })),
    ...webModules.flatMap((m) =>
      (m.commands ?? []).filter((c) => can(c.needs)).map((c) => ({ id: c.id, label: c.label, hint: c.hint, keywords: c.keywords, group: "Actions", onSelect: () => navigate({ to: c.to }) })),
    ),
    ...(can("signet.assistant:view") ? [{ id: "assistant", label: "Ask the assistant", group: "Actions", shortcut: "J", icon: <SparkIcon size={14} />, onSelect: () => setAssistant(true) }] : []),
    { id: "theme", label: theme === "light" ? "Dark mode" : "Light mode", keywords: "theme appearance", group: "Account", icon: theme === "light" ? <MoonIcon size={14} /> : <SunIcon size={14} />, onSelect: switchTheme },
    { id: "settings", label: "Settings", group: "Account", icon: <SettingsIcon size={14} />, onSelect: () => navigate({ to: "/settings" }) },
    { id: "signout", label: "Sign out", group: "Account", icon: <LogoutIcon size={14} />, onSelect: signOut },
  ];

  const active = (to: string) => (to === "/" ? pathname === "/" : pathname === to || pathname.startsWith(`${to}/`));
  const holdsActive = (p: NavPlace) => (isGroup(p) ? p.places.some((q) => active(q.to)) : !isSoon(p) && active(p.to));
  const goTo = (p: Place): MenuItem => ({ kind: "item" as const, label: p.label, icon: <p.icon size={14} />, onSelect: () => navigate({ to: p.to }) });

  return (
    <div ref={frame.shell} className={`shell${assistant ? " with-assistant" : ""}${isMacDesktop ? " mac-desktop" : ""}`}>
      {viewingAs ? (
        <div className="signet-view-as-bar" role="status">
          <EyeIcon size={16} />
          <span><strong>Viewing as {viewingAs.label}</strong><small>{viewingAs.kind === "role" ? "Role preview" : "User preview"}. Preview is read only.</small></span>
          <button type="button" className="secondary-button tiny" onClick={stopViewAs}>Stop preview</button>
        </div>
      ) : null}
      <header ref={frame.bar} className="topbar signet-topbar">
        <Link to="/" className="topbar-brand" aria-label="Signet home">
          <span className="signet-coin"><SignetMark size={26} /></span>
          <span className="topbar-name"><strong>Signet</strong><ByNumero /></span>
        </Link>
        <nav className="nav" aria-label="Main">
          {inNav.map((p) =>
            isGroup(p) ? (
              <MenuButton
                key={`group:${p.group}`}
                label={p.label}
                className={`nav-more signet-nav-group${holdsActive(p) ? " on" : ""}`}
                placement="bottom-start"
                items={p.places.map(goTo)}
              >
                <p.icon size={14} />
                {p.label}
                <ChevronIcon size={11} />
              </MenuButton>
            ) : isSoon(p) ? (
              <span key={`soon:${p.label}`} className="signet-nav-soon" aria-disabled="true">
                <p.icon size={14} />
                {p.label}
                <span className="signet-soon-tag">Coming soon</span>
              </span>
            ) : (
              <Link key={p.to} to={p.to} className={active(p.to) ? "on" : ""} aria-current={active(p.to) ? "page" : undefined}>
                <p.icon size={14} />
                {p.label}
              </Link>
            ),
          )}
          {overflow.length ? (
            <MenuButton
              label="More places"
              className={`nav-more${overflow.some(holdsActive) ? " on" : ""}`}
              placement="bottom-start"
              items={overflow.flatMap((p): MenuItem[] =>
                isGroup(p)
                  ? [{ kind: "section" as const, label: p.label }, ...p.places.map(goTo)]
                  : isSoon(p)
                    ? [{ kind: "item" as const, label: p.label, icon: <p.icon size={14} />, hint: "Coming soon", disabled: true, onSelect: () => {} }]
                    : [goTo(p)],
              )}
            >
              More
              <ChevronIcon size={11} />
            </MenuButton>
          ) : null}
        </nav>
        <div className="spacer" />
        <button type="button" className="search-trigger" onClick={() => setPalette(true)} aria-label="Search and commands">
          {/* A phone has no keyboard to press K on, so it gets the icon instead (styles.css). */}
          <SearchIcon size={15} className="signet-search-icon" />
          <span>Search or jump to</span>
          <kbd>{navigator.platform.includes("Mac") ? "⌘" : "Ctrl"} K</kbd>
        </button>
        {isDesktop ? (
          <button
            type="button"
            className="icon-button signet-reload"
            onClick={reloadApp}
            aria-label={newer ? "Reload for the new version of Signet" : "Reload Signet"}
            title={newer ? "A new version of Signet is ready. Reload to get it." : "Reload Signet (Cmd+Shift+R)"}
          >
            <RefreshIcon size={15} />
            {newer ? <span className="signet-reload-dot" aria-hidden="true" /> : null}
          </button>
        ) : null}
        {can("signet.assistant:view") ? (
          <button type="button" className={`icon-button${assistant ? " on" : ""}`} onClick={() => setAssistant((open) => !open)} aria-label="Assistant" aria-pressed={assistant}>
            <SparkIcon size={16} />
          </button>
        ) : null}
        <MenuButton
          label="Account"
          className="topbar-user"
          items={[
            { kind: "section", label: me?.user.name ?? me?.user.username ?? "" },
            { kind: "item", label: "Settings", icon: <SettingsIcon size={14} />, onSelect: () => navigate({ to: "/settings" }) },
            { kind: "item", label: theme === "light" ? "Dark mode" : "Light mode", icon: theme === "light" ? <MoonIcon size={14} /> : <SunIcon size={14} />, onSelect: switchTheme },
            { kind: "divider" },
            { kind: "item", label: "Sign out", icon: <LogoutIcon size={14} />, onSelect: signOut },
          ]}
        >
          <Avatar name={me?.user.name ?? me?.user.username ?? "?"} size="small" />
          <span className="truncate">{me?.user.name ?? me?.user.username}</span>
        </MenuButton>
      </header>
      <main className="content" id="main">
        <Outlet />
      </main>
      <CommandPalette commands={commands} open={palette} onOpenChange={setPalette} placeholder="Go to a page or run a command" />
      {can("signet.assistant:view") ? <AssistantPanel open={assistant} onClose={() => setAssistant(false)} /> : null}
    </div>
  );
}
